import { merge } from 'lodash-es';
import {
  type ComponentProps,
  type RefObject,
  useCallback,
  useLayoutEffect,
  useRef,
  useState,
} from 'react';
import { mergeRefs } from 'react-merge-refs';

import type { GutterContext, GutterProp } from '../../utilities/shared/Gutter';
import type { SpacingScale } from '../../utilities/shared/sizes';
import { Divider } from '../../assets/Divider/Divider';
import { LoadingIcon } from '../../assets/LoadingIcon/LoadingIcon';
import { backgrounds } from '../../common/backgrounds';
import { sizing } from '../../common/sizing';
import { zIndexes } from '../../common/z_indexes';
import { colors, darkThemeSelector, shadows, styled } from '../../stitches.config';
import { type FadeEdge, fadeMaskCSS } from '../../utilities/shared/FadeMask';
import { getGutterTokens, gutterCSS } from '../../utilities/shared/Gutter';
import { maxWidthCSS } from '../../utilities/shared/MaxWidth';
import { minWidthCSS } from '../../utilities/shared/MinWidth';
import { spacingCSS } from '../../utilities/shared/Spacing';
import { widthCSS } from '../../utilities/shared/Width';
import { StuckProvider } from '../../utilities/useStuck';
import {
  type ShellPropContentMode,
  type ShellPropSize,
  type ShellPropType,
  ShellLayerScrollProvider,
  useShellContentMode,
  useShellMaxWidth,
  useShellMinWidth,
  useShellSize,
  useShellType,
  useShellWidth,
  useShellWidthMode,
} from './utils';

const ShellContentStuckInnerPosition = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  height: 'fit-content',

  variants: {
    stuck: {
      true: {
        background: colors.bgApplicationLight,
        boxShadow: shadows.stuckLight,

        [darkThemeSelector]: {
          background: colors.bgApplicationDark,
          boxShadow: shadows.stuckDark,
        },
      },
      false: {
        '&::after': {
          content: '',
          position: 'absolute',
          bottom: '-0.5px',
          display: 'block',
          height: '$1',
          background: colors.strokeNeutralLight,

          [darkThemeSelector]: {
            background: colors.strokeNeutralDark,
          },
        },
      },
    },
    shellSize: {
      'x-small': {},
      small: {},
      medium: {},
    },
  },

  compoundVariants: [
    {
      stuck: false,
      shellSize: 'x-small',
      css: {
        '&::after': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },
      },
    },
    {
      stuck: false,
      shellSize: 'small',
      css: {
        '&::after': {
          right: sizing.contentSides,
          left: sizing.contentSides,
        },
      },
    },
    {
      stuck: false,
      shellSize: 'medium',
      css: {
        '&::after': {
          right: sizing.sides,
          left: sizing.sides,
        },
      },
    },
  ],

  defaultVariants: {
    shellSize: 'medium',
  },
});
ShellContentStuckInnerPosition.displayName = 'ShellContentStuckInnerPosition';

const ShellContentStuckInnerContainer = styled('div', {
  gridArea: 'stuck',
  display: 'flex',
  width: '100%',
});
ShellContentStuckInnerContainer.displayName = 'ShellContentStuckInnerContainer';

type ShellContentGlanceProps = {
  /**
   * Provide the content for the glance.
   */
  children: React.ReactNode;
  /**
   * Boolean to determine if the element is stuck or not.
   */
  stuck?: boolean;
  /**
   * Ref to the element.
   */
  ref?: React.Ref<HTMLDivElement | null>;
};

export function ShellContentStuckInner({ children, stuck, ref }: ShellContentGlanceProps) {
  const shellSize = useShellSize(undefined, 'medium');

  return (
    <StuckProvider value={stuck}>
      <ShellContentStuckInnerContainer ref={ref}>
        <ShellContentStuckInnerPosition stuck={stuck} shellSize={shellSize}>
          {children}
        </ShellContentStuckInnerPosition>
      </ShellContentStuckInnerContainer>
    </StuckProvider>
  );
}

const useShellContentStuck = () => {
  const [isOutOfView, setIsOutOfView] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  // Make the sticky header visible when 70% of the "real" header is out of view.
  useLayoutEffect(() => {
    const callback = (entries: IntersectionObserverEntry[]) => {
      // ensures that the element is rendered with content without display: none; (a result of AppSkeleton)
      if (ref.current?.clientHeight) {
        setIsOutOfView(!entries[0].isIntersecting);
      }
    };

    const observer = new IntersectionObserver(callback, {
      root: null,
      threshold: 0.3,
      rootMargin: '0px 0px 0px 0px',
    });

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => {
      observer?.disconnect();
    };
  }, []);

  return { ref, isOutOfView };
};

const ShellContentStuckZero = styled('div', {
  gridArea: 'stuck',
  position: 'sticky',
  zIndex: zIndexes.paneStuck,
  top: 0,
  height: 0,
  opacity: 0,
  transition: 'opacity 200ms ease-in-out',

  variants: {
    visible: {
      true: {
        display: 'flex',
        opacity: 1,
      },
      false: {
        opacity: 0,
        pointerEvents: 'none',
      },
    },
  },
});
ShellContentStuckZero.displayName = 'ShellContentStuckZero';

export function ShellContentStuck({
  spacing,
  ...props
}: { spacing: SpacingScale } & ShellContentGlanceProps) {
  const { ref, isOutOfView } = useShellContentStuck();

  return (
    <>
      <ShellContentStuckZero
        visible={isOutOfView}
        aria-hidden="true"
        style={{ marginTop: `-${spacing}px` }}
      >
        <ShellContentStuckInner {...props} stuck />
      </ShellContentStuckZero>
      <ShellContentStuckInner {...props} stuck={false} ref={ref} />
    </>
  );
}

const ShellContentDiagram = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentDiagram.displayName = 'ShellContentDiagram';

const ShellContentGlance = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentGlance.displayName = 'ShellContentGlance';

const ShellContentControls = styled('div', {
  position: 'relative',
  display: 'flex',
});
ShellContentControls.displayName = 'ShellContentControls';

export const ShellContentScroll = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  overflow: 'auto',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
        minHeight: 0,
      },
    },
    widthMode: {
      centered: {
        alignSelf: 'center',
      },
      full: {},
    },
  },
});
ShellContentScroll.displayName = 'ShellContentScroll';

const ShellContentInset = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  minWidth: 0,
  boxSizing: 'border-box',

  variants: {
    contentMode: {
      fit: {
        minHeight: 'min-content',
      },
      stretch: {
        flex: '1 1 auto',
        minHeight: 0,
      },
    },
  },

  defaultVariants: {
    contentMode: 'stretch',
  },
});
ShellContentInset.displayName = 'ShellContentInset';

const ShellContentStatic = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'stretch',
  height: 'fit-content',

  variants: {
    widthMode: {
      centered: {
        alignSelf: 'center',
      },
      full: {},
    },
  },
});
ShellContentStatic.displayName = 'ShellContentStatic';

const ShellContentPosition = styled('div', {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    contentMode: {
      fit: {
        height: 'fit-content',
      },
      stretch: {
        height: '100%',
      },
    },
    variant: {
      scroll: {},
      static: {
        flex: '1 0 auto',
        heightAll: 'min-content',

        '@notDesktop': {
          width: '100%',
        },
      },
    },
  },
});
ShellContentPosition.displayName = 'ShellContentPosition';

const ShellContentDivider = styled(Divider, {
  variants: {
    shellSize: {
      'x-small': {
        padding: `0 ${sizing.contentSides}`,
      },
      small: {
        padding: `0 ${sizing.contentSides}`,
      },
      medium: {
        padding: `0 ${sizing.sides}`,
      },
    },
  },
  defaultVariants: {
    shellSize: 'medium',
  },
});
ShellContentDivider.displayName = 'ShellContentDivider';

const shellContentPatternedBackground = {
  checkered: {
    background: backgrounds.checkeredLight,
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      background: backgrounds.checkeredDark,
      backgroundColor: colors.bgApplicationDark,
    },
  },
  dotted: {
    background: backgrounds.dottedLight,
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      background: backgrounds.dottedDark,
      backgroundColor: colors.bgApplicationDark,
    },
  },
};

const shellContentLoadingWashBackground = {
  ...shellContentPatternedBackground,
  default: {
    backgroundColor: colors.bgApplicationLight,

    [darkThemeSelector]: {
      backgroundColor: colors.bgApplicationDark,
    },
  },
};

const ShellContentContainer = styled('div', {
  gridArea: 'shell-content',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0,
  overflow: 'hidden',

  variants: {
    background: {
      ...shellContentPatternedBackground,
      default: {},
    },
  },
});
ShellContentContainer.displayName = 'ShellContentContainer';

const ShellContentLoadingOverlay = styled('div', {
  position: 'absolute',
  inset: 0,
  zIndex: zIndexes.contentLoading,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  opacity: 0.8,

  variants: {
    background: shellContentLoadingWashBackground,
  },

  defaultVariants: {
    background: 'default',
  },
});
ShellContentLoadingOverlay.displayName = 'ShellContentLoadingOverlay';

export type ShellContentProps = {
  /**
   * Set whether the background should be checkered or not.
   */
  background?: 'default' | 'checkered' | 'dotted';
  /**
   * Pass in any content as `children`.
   */
  children?: React.ReactNode;
  /**
   * When true, covers the shell content (including controls and scroll area) with a loading wash
   * and spinner. The wash matches {@link ShellContentProps.background} when set.
   */
  loading?: boolean;
  /**
   * Set the content mode for the content.
   */
  contentMode?: ShellPropContentMode;
  /**
   * Provide controls for the content.
   */
  controls?: React.ReactNode;
  /**
   * Provide diagram for the content, which goes before controls.
   */
  diagram?: React.ReactNode;
  /**
   * Show a masking gradient at the edges of the container.
   * When `gutter`, the fade matches the `gutter` property.
   */
  fade?: FadeEdge | 'gutter';
  /**
   * Provide glance for the content, which goes after controls.
   */
  glance?: React.ReactNode;
  /**
   * Provide a size for your shell.
   * Note: Automatically provided from the `Shell` component, you likely don't need to provide this.
   */
  size?: ShellPropSize;
  /**
   * Shell frame type for container queries (`container-name` / `containers.*` in atto).
   * Inherits from `Shell` via context when omitted. Pane/Drawer wrappers set this explicitly.
   */
  type?: ShellPropType;
  /**
   * Set the spacing between the children.
   */
  spacing?: SpacingScale;
  /**
   * Ref to the scroll container.
   */
  scrollRef?: RefObject<HTMLDivElement | null>;
} & GutterProp &
  Omit<ComponentProps<typeof ShellContentContainer>, 'type'>;

export function ShellContent({
  background,
  children,
  contentMode,
  controls,
  diagram,
  fade = 'none',
  glance,
  gutter = { edge: 'all' },
  loading,
  ref,
  scrollRef,
  size,
  spacing = 16,
  type,
  ...remaining
}: ShellContentProps) {
  const shellContentMode = useShellContentMode(contentMode);
  const shellSize = useShellSize(size, 'medium');
  const shellType = useShellType(type);
  const shellWidth = useShellWidth();
  const shellMaxWidth = useShellMaxWidth();
  const shellMinWidth = useShellMinWidth();
  const shellWidthMode = useShellWidthMode();
  const innerScrollRef = useRef<HTMLDivElement>(null);
  const [scrollContainer, setScrollContainer] = useState<HTMLElement | null>(null);

  const scrollContainerSyncRef = useCallback((node: HTMLDivElement | null) => {
    setScrollContainer(node);
  }, []);

  const shellContentSpacingRef = mergeRefs([scrollRef ?? innerScrollRef, scrollContainerSyncRef]);

  let gutterContext: GutterContext = 'application';
  if (shellSize === 'small' || shellSize === 'x-small') {
    gutterContext = 'content';
  }

  const { ends: fadeSizeToken } = getGutterTokens(gutterContext, gutter.edge);
  const effectiveFade = fade === 'gutter' ? gutter.edge : fade;

  const hasWidth = shellWidth || shellMaxWidth || shellMinWidth;

  const css = merge(
    shellWidth ? widthCSS(shellWidth) : undefined,
    shellMaxWidth ? maxWidthCSS(shellMaxWidth) : undefined,
    shellMinWidth ? minWidthCSS(shellMinWidth) : undefined,
  );

  const insetCss = merge(spacingCSS(spacing), gutterCSS({ context: gutterContext, ...gutter }));

  const scrollSurfaceCss = merge(
    effectiveFade ? fadeMaskCSS(fadeSizeToken, effectiveFade) : undefined,
    hasWidth ? css : undefined,
  );

  const { css: cssProp, ...restRemaining } = remaining;

  return (
    <ShellContentContainer
      ref={ref}
      background={background}
      aria-busy={loading ? true : undefined}
      css={merge(
        {
          containerType: 'inline-size',
          containerName: shellType,
        },
        cssProp,
      )}
      {...restRemaining}
    >
      {controls || glance ? (
        <ShellContentPosition variant="static">
          <ShellContentStatic widthMode={shellWidthMode} css={css}>
            {diagram && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentDiagram>{diagram}</ShellContentDiagram>
              </>
            )}
            {controls && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentControls>{controls}</ShellContentControls>
              </>
            )}
            {glance && (
              <>
                <ShellContentDivider shellSize={shellSize} />
                <ShellContentGlance>{glance}</ShellContentGlance>
              </>
            )}
            <ShellContentDivider shellSize={shellSize} />
          </ShellContentStatic>
        </ShellContentPosition>
      ) : null}
      <ShellContentPosition contentMode={shellContentMode}>
        <ShellLayerScrollProvider scrollContainer={scrollContainer}>
          <ShellContentScroll
            ref={shellContentSpacingRef}
            contentMode={shellContentMode}
            widthMode={shellWidthMode}
            css={scrollSurfaceCss}
          >
            <ShellContentInset contentMode={shellContentMode} css={insetCss}>
              {children}
            </ShellContentInset>
          </ShellContentScroll>
        </ShellLayerScrollProvider>
      </ShellContentPosition>
      {loading ? (
        <ShellContentLoadingOverlay
          background={background ?? 'default'}
          role="status"
          aria-live="polite"
          aria-label="Loading"
        >
          <LoadingIcon size={20} variant="neutral" />
        </ShellContentLoadingOverlay>
      ) : null}
    </ShellContentContainer>
  );
}
